<template>
    <div class="dashboard-pro">
        <v-row class="d-md-flex align-center mb-6">
            <v-col cols="6">
                <h1>{{ $t("dashboard") }} Pro</h1>
            </v-col>
            <v-col cols="6" class="filter-box d-flex justify-end">
                <v-btn @click="downloadCSV" class="btn-two mr-3" color="#08a657" height="56px" depressed>
                    {{ $t("dash.exportData") }}
                </v-btn>
                <v-btn class="btn-two" color="#08a657" height="56px" depressed
                    @click="openCompare()"
                >
                    <v-icon medium>mdi-compare</v-icon>
                    {{ $t('compare') }}
                </v-btn>
            </v-col>
        </v-row>
        <v-row>
            <v-col class="py-0">
                <v-chip
                    v-if="dateTimeBegin && dateTimeEnd"
                    class="ma-1"
                    close
                    @click:close="removeFilterDate()"
                    small
                >
                    <strong>{{ $t("dash.dateRange") }}</strong>: {{ dateTimeBegin }} / {{ dateTimeEnd }}
                </v-chip>
                <v-chip
                    v-if="selectedOrganization.id"
                    class="ma-1"
                    close
                    @click:close="removeFilterOrganisation()"
                    small
                >
                    <strong>{{ $t("organizations") }}</strong>: {{ organisationName }}
                </v-chip>
                <v-chip
                    v-if="(dateTimeBegin && dateTimeEnd) || selectedOrganization.id"
                    class="ma-1"
                    close
                    @click:close="removeFilterAll()"
                    small
                    outlined
                >
                    <strong>{{ $t("clear") }}</strong>
                </v-chip>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="2">
                <v-btn
                    class="btn-two mb-12"
                    color="#08a657"
                    height="56px"
                    width="100%"
                    @click="applyFilter()"
                >
                    <span>{{ $t("apply") }}</span>
                </v-btn>
                <v-menu
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    max-width="290px"
                    min-width="290px"
                >
                    <template v-slot:activator="{ on }">
                        <v-text-field
                            v-model="filterDateRangeText"
                            :label="$t('dash.dateRange')"
                            readonly
                            v-on="on"
                        ></v-text-field>
                    </template>
                    <v-date-picker
                        v-model="filterDates"
                        range
                        :locale="$i18n.locale"
                        color="primary"
                        no-title
                    >
                    </v-date-picker>
                </v-menu>
                <v-autocomplete
                    class="mb-5"
                    v-if="isLoginUserSuperAdmin()"
                    v-model="selectedOrganization"
                    :items="allOrganisations"
                    item-text="name"
                    return-object
                    :label="$t('anOrganization')"
                    outlined
                    :no-data-text="$t('noDataAvailable')"
                    hide-details
                    item-value="id"
                >
                    <template v-slot:item="data">
                        <div>
                            <div><b>{{data.item.name}}</b></div>
                            <div>
                                <span v-if="data.item.street">{{data.item.street}}</span>
                                <span v-if="data.item.postalCode">, {{data.item.postalCode}}</span>
                                <span v-if="data.item.place">, {{data.item.place}}</span>
                                <span v-if="data.item.countryCode">, {{data.item.countryCode}}</span>
                            </div>
                        </div>
                    </template>
                </v-autocomplete>

                <v-checkbox
                    v-if="isLoginUserSuperAdmin()"
                    :label="$t('dash.wholeHierarchy')"
                    v-model="subsidiaries"
                    color="primary"
                    hide-details
                    :disabled="!selectedOrganization.id"
                ></v-checkbox>
                <v-checkbox
                    v-if="isLoginUserSuperAdmin()"
                    :label="$t('dash.onlyCarrier')"
                    v-model="onlyCarrier"
                    color="primary"
                    hide-details
                    :disabled="!selectedOrganization.id"
                ></v-checkbox>
                <v-checkbox
                    v-if="isLoginUserSuperAdmin()"
                    :label="$t('dash.onlySender')"
                    v-model="onlySender"
                    color="primary"
                    hide-details
                    :disabled="!selectedOrganization.id"
                ></v-checkbox>
                <v-checkbox
                    v-if="isLoginUserSuperAdmin()"
                    :label="$t('dash.onlyReceiver')"
                    v-model="onlyReceiver"
                    color="primary"
                    hide-details
                    :disabled="!selectedOrganization.id"
                    class="mb-5"
                ></v-checkbox>

                <v-text-field
                    v-if="isLoginUserSuperAdmin()"
                    v-model="carrierCode"
                    :label="$t('dash.carrierCode')"
                    outlined
                    hide-details
                >
                </v-text-field>

                <div v-for="checkbox in filteringCheckboxes" :key="checkbox.id">
                    <v-checkbox
                        :label="$t(checkbox.name)"
                        :append="1"
                        v-model="checkbox[checkbox.id]"
                        :disabled="checkbox.disabled"
                        color="primary"
                        hide-details
                        @change="(val) => signatureTypeInactive(val, checkbox.id)"
                    >
                        <template v-slot:label>
                            <div>
                                {{ $t(checkbox.name) }}
                                ({{ dashboardStatistics[checkbox.id === "created" ? "count" : `${checkbox.id}Count`] }})
                            </div>
                        </template>
                    </v-checkbox>
                    <div v-if="checkbox.children" class="pl-3">
                        <v-checkbox
                            v-for="child in checkbox.children"
                            :key="child.id"
                            v-model="child[child.id]"
                            input-value="true"
                            :disabled="!checkbox[checkbox.id]"

                            color="primary"
                            hide-details
                        >
                            <template v-slot:label>
                                <div v-if="checkbox.id !== 'exceptions'">
                                    {{ $t(child.name) }}
                                    ({{ dashboardStatistics[child.id] }})
                                </div>
                                <div v-if="checkbox.id === 'exceptions'">
                                    {{ child.name }}
                                    ({{ child.count }})
                                </div>
                            </template>
                        </v-checkbox>
                    </div>
                </div>
            </v-col>
            <v-col cols="10">
                <v-card>
                    <v-card-title>
                        <v-spacer></v-spacer>
                        <div>
                            <v-select
                                v-model="selectedHeaders"
                                :items="headers"
                                :label="$t('dash.selectColumns')"
                                @change="sortingSelectedHeaders"
                                multiple
                                outlined
                                return-object
                            >
                                <template v-slot:selection="{ item, index }">
                                    <v-chip v-if="index < 2">
                                        <span>{{ item.text }}</span>
                                    </v-chip>
                                    <span v-if="index === 2" class="grey--text caption">
                                        (+{{ selectedHeaders.length - 2 }} {{ $t('dash.others') }})
                                    </span>
                                </template>
                            </v-select>
                        </div>
                    </v-card-title>
                    <v-data-table
                        :options.sync="options"
                        :server-items-length="totalItems"
                        :loading="this.$store.state.loading"
                        :no-data-text="$t('noDataAvailable')"
                        :sort-by.sync="options.sortBy"
                        :sort-desc.sync="options.lastModified"
                        :headers="showHeaders"
                        :items="dashboardCmrData"
                        :items-per-page="options.rowsPerPageItems"
                        :footer-props="{
                        'items-per-page-options': this.$store.state.rowsPerPageItems,
                        'items-per-page-text': $t('rowsPerPage'),
                        }"
                    >
                        <template v-slot:item.status="{ item }">
                            <span :style="statusNameColor(item)">{{ item.statusName }}</span>
                        </template>
                        <template v-slot:item.countAttachments="{ item }">
                            <v-btn :disabled="item.countAttachments === 0" fab small @click="openAttachmentsDialog(item.id)">
                                <span> {{ item.countAttachments }} </span>
                            </v-btn>
                        </template>
                    </v-data-table>
                </v-card>
                    <!-- <div v-if="Object.keys(chartSettings).length" class="pt-15">
                        <v-expansion-panels accordion>
                            <v-expansion-panel>
                                <v-expansion-panel-header>
                                    <strong>Сhart settings</strong>
                                </v-expansion-panel-header>
                                <v-expansion-panel-content>
                                    <v-row>
                                        <v-col>
                                            <p>CMR statuses</p>

                                            <v-radio-group v-model="selectedChartType">
                                                <v-radio
                                                    v-for="chartType in chartTypes"
                                                    :key="chartType.label"
                                                    :label="chartType.label"
                                                    :value="chartType.value"
                                                    color="primary"
                                                ></v-radio>
                                            </v-radio-group>
                                        </v-col>
                                    </v-row>
                                </v-expansion-panel-content>
                            </v-expansion-panel>
                        </v-expansion-panels>
                    </div>
                    <div v-if="chartsData.length">
                        <vuetiful-board
                            :col-num="24"
                            :row-height="30"
                            :layout-editable="true"
                            :datasets="chartsData"
                        />
                    </div> -->

            </v-col>
        </v-row>

        <v-dialog v-model="attchmentsDialog" max-width="600">
            <v-card>
                <v-card-title class="justify-space-between">
                    {{ $t("crm.attachments") }}
                <v-btn @click="closeAttachmentsDialog()" text icon color="#5b8ff5">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
                </v-card-title>
                <v-list>
                    <v-list-item v-for="document in documentList" :key="document.id">
                        <v-list-item-action>
                            <v-icon v-if="document.type === 2" large>mdi-file-image-outline</v-icon>
                            <v-icon v-else-if="document.type === 1" large>mdi-file-pdf-outline</v-icon>
                            <v-icon v-else-if="document.type === 3" large>mdi-file-document-outline</v-icon>
                        </v-list-item-action>

                        <v-list-item-content>
                            <v-list-item-title>{{ document.name }}</v-list-item-title>
                        </v-list-item-content>
                        <v-list-item-action>
                            <v-btn @click="downloadDocument(document.id, document.type, document.name)" depressed color="primary">
                                {{ $t("download") }}
                                <v-icon>mdi-download</v-icon>
                            </v-btn>
                        </v-list-item-action>
                    </v-list-item>
                </v-list>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
    import config from "../../config";
    import Axios from "axios";
    import { mapActions } from "vuex";

    const qs = require("querystring");

    export default {
        name: "DashboardPro",
        data() {
            return {
                dashboardStatistics: {},
                filteringCheckboxes: [
                    {
                    id: "created",
                    name: "dash.cmrs",
                    created: false,
                    disabled: false,
                    },
                    {
                    id: "assigned",
                    name: "dash.assigned",
                    assigned: false,
                    disabled: false,
                    },
                    {
                    id: "signedBySender",
                    name: "dash.signedBySender",
                    signedBySender: false,
                    disabled: false,
                    children: [
                        {
                        id: "senderSignedBySignature",
                        name: "dash.signedBySignature",
                        senderSignedBySignature: false,
                        disabled: false,
                        },
                        {
                        id: "senderSignedByQRCode",
                        name: "dash.signedByQRCode",
                        senderSignedByQRCode: false,
                        disabled: false,
                        },
                        {
                        id: "senderSignedWithoutPresence",
                        name: "dash.signedWithoutPresence",
                        senderSignedWithoutPresence: false,
                        disabled: false,
                        },
                        {
                        id: "senderSignedViaPortal",
                        name: "dash.signedViaPortal",
                        senderSignedViaPortal: false,
                        disabled: false,
                        },
                        {
                        id: "senderSignedByDefaultUser",
                        name: "dash.signedByDefaultUser",
                        senderSignedByDefaultUser: false,
                        disabled: false,
                        },
                        {
                        id: "senderSignedBySubordinateUser",
                        name: "dash.signedBySubordinateUser",
                        senderSignedBySubordinateUser: false,
                        disabled: false,
                        },
                    ],
                    },
                    {
                    id: "signedByDriver",
                    name: "dash.signedByDriver",
                    signedByDriver: false,
                    disabled: false,
                    children: [
                        {
                        id: "driverSignedBySignature",
                        name: `dash.signedBySignature`,
                        driverSignedBySignature: false,
                        disabled: false,
                        },
                        {
                        id: "driverSignedByQRCode",
                        name: "dash.signedByQRCode",
                        driverSignedByQRCode: false,
                        disabled: false,
                        },
                        {
                        id: "driverSignedWithoutPresence",
                        name: "dash.signedWithoutPresence",
                        driverSignedWithoutPresence: false,
                        disabled: false,
                        },
                        {
                        id: "driverSignedViaPortal",
                        name: "dash.signedViaPortal",
                        driverSignedViaPortal: false,
                        disabled: false,
                        },
                        {
                        id: "driverSignedByDefaultUser",
                        name: "dash.signedByDefaultUser",
                        driverSignedByDefaultUser: false,
                        disabled: false,
                        },
                        {
                        id: "driverSignedBySubordinateUser",
                        name: "dash.signedBySubordinateUser",
                        driverSignedBySubordinateUser: false,
                        disabled: false,
                        },
                    ],
                    },
                    {
                    id: "signedByReceiver",
                    name: "dash.signedByReceiver",
                    signedByReceiver: false,
                    disabled: false,
                    children: [
                        {
                        id: "receiverSignedBySignature",
                        name: "dash.signedBySignature",
                        receiverSignedBySignature: false,
                        disabled: false,
                        },
                        {
                        id: "receiverSignedByQRCode",
                        name: "dash.signedByQRCode",
                        receiverSignedByQRCode: false,
                        disabled: false,
                        },
                        {
                        id: "receiverSignedWithoutPresence",
                        name: "dash.signedWithoutPresence",
                        receiverSignedWithoutPresence: false,
                        disabled: false,
                        },
                        {
                        id: "receiverSignedViaPortal",
                        name: "dash.signedViaPortal",
                        receiverSignedViaPortal: false,
                        disabled: false,
                        },
                        {
                        id: "receiverSignedByDefaultUser",
                        name: "dash.signedByDefaultUser",
                        receiverSignedByDefaultUser: false,
                        disabled: false,
                        },
                        {
                        id: "receiverSignedBySubordinateUser",
                        name: "dash.signedBySubordinateUser",
                        receiverSignedBySubordinateUser: false,
                        disabled: false,
                        },
                    ],
                    },
                    {
                    id: "latePickup",
                    name: "dash.latePickup",
                    latePickup: false,
                    disabled: false,
                    },
                    {
                    id: "lateDelivery",
                    name: "dash.lateDelivery",
                    lateDelivery: false,
                    disabled: false,
                    },
                    {
                    id: "senderRemarks",
                    name: "dash.remarksSender",
                    senderRemarks: false,
                    disabled: false,
                    },
                    {
                    id: "driverRemarks",
                    name: "dash.remarksDriver",
                    driverRemarks: false,
                    disabled: false,
                    },
                    {
                    id: "receiverRemarks",
                    name: "dash.remarksReceiver",
                    receiverRemarks: false,
                    disabled: false,
                    },
                    {
                    id: "exceptions",
                    name: "organization.exceptions",
                    exceptions: false,
                    disabled: false,
                    children: []
                    },
                ],

                selectedHeaders: [],
                documentList: [],

                options: {
                    sortBy: ["lastModified"],
                    sortDesc: ["desc"],
                    rowsPerPageItems: 10,
                    page: 1,
                    itemsPerPage: 10,
                },

                allOrganisations: [],
                filterDates: [],
                dateTimeBegin: "",
                dateTimeEnd: "",
                selectedOrganization: {},
                attchmentsDialog: false,

                subsidiaries: false,
                onlySender: false,
                onlyCarrier: false,
                onlyReceiver: false,
                carrierCode: "",

                // statusStatistics: [],
                // chartTypes: [
                //     {
                //         label: 'Pie chart',
                //         value: 'pie',
                //         // iconClass: 'mdi mdi-chart-pie'
                //     },
                //     {
                //         label: 'Bar chart',
                //         value: 'bar',
                //         // iconClass: 'mdi mdi-chart-bar'
                //     },
                //     {
                //         label: 'Column chart',
                //         value: 'column',
                //         // iconClass: 'mdi mdi-chart-bar'
                //     },
                //     {
                //         label: 'Treemap chart',
                //         value: 'treemap',
                //         // iconClass: 'mdi mdi-chart-tree'
                //     }

                // ],


                // chartSettings: {},

                // selectedChartType: localStorage.getItem('selectedChartType') || 'pie',
                // chartsData : []
            };
        },
        computed: {
            dashboardCmrData() {
                let me = this
                let data = []
                let statusName = ''

                const cmrList = JSON.parse(JSON.stringify(this.$store.getters.DASHBOARD_CMR_LIST))
                cmrList.forEach(function(item) {
                    switch (item.status) {
                        case 1:
                            statusName = me.$i18n.t('crm.status_created')
                            break
                        case 2:
                            statusName = me.$i18n.t('crm.status_ready_signing')
                            break
                        case 3:
                            statusName = me.$i18n.t('crm.status_signature_shipper')
                            break
                        case 4:
                            statusName = me.$i18n.t('crm.status_signature_driver')
                            break
                        case 5:
                            statusName = me.$i18n.t('crm.status_signature_receiver')
                            break
                        case 6:
                            statusName = me.$i18n.t('crm.status_mailing')
                            break
                        case 7:
                            statusName = me.$i18n.t('crm.status_closed')
                            break
                        case 8:
                            statusName = me.$i18n.t('crm.status_removed')
                            break
                        case 9:
                            statusName = me.$i18n.t('crm.status_unfinished')
                            break
                        default:
                            statusName = ''
                            break
                    }

                    item.lastModified = me.$utcToCurrentDate(item.lastModified);
                    item.collectionDate = me.$utcToCurrentDate(item.collectionDate);
                    item.deliveryDate = me.$utcToCurrentDate(item.deliveryDate);
                    item.startLoadingTime = me.$utcToCurrentDate(item.startLoadingTime);
                    item.stopLoadingTime = me.$utcToCurrentDate(item.stopLoadingTime);
                    item.startUnloadingTime =me.$utcToCurrentDate(item.startUnloadingTime);
                    item.stopUnloadingTime = me.$utcToCurrentDate(item.stopUnloadingTime);

                    item.statusName = statusName;
                    data.push(item);
                })
                return data
            },
            payload() {
                return { Page: this.options.page, Limit: this.options.itemsPerPage, OrderBy: "CMRCode", Direction: "desc" };
            },
            totalItems() {
                return this.$store.getters.DASHBOARD_CMR_TOTAL;
            },
            headers() {
                return [
                    { text: this.$i18n.t("dash.cmrCode"), value: "cmrCode", width: "1%", align: "left", sortable: false, order:"1" },
                    { text: this.$i18n.t("dash.status"), value: "status", width: "1%", align: "left", sortable: false, order:"2" },
                    { text: this.$i18n.t("dash.lastModified"), value: "lastModified", width: "1%", align: "left", sortable: false, order:"3" },
                    { text: this.$i18n.t("dash.driver"), value: "driver", width: "1%", align: "left", sortable: false, order:"4" },
                    { text: this.$i18n.t("dash.tripID"), value: "tripID", width: "1%", align: "left", sortable: false, order:"5" },

                    { text: this.$i18n.t("dash.carrierCode"), value: "carrierCode", width: "1%", align: "left", sortable: false, order:"6" },
                    { text: this.$i18n.t("dash.carrierName"), value: "carrierName", width: "1%", align: "left", sortable: false, order:"7" },

                    { text: this.$i18n.t("dash.collectionDate"), value: "collectionDate", width: "1%", align: "left", sortable: false, order:"8" },
                    { text: this.$i18n.t("dash.deliveryDate"), value: "deliveryDate", width: "1%", align: "left", sortable: false, order:"9" },

                    { text: this.$i18n.t("dash.minutesLatePickup"), value: "minutesLatePickup", width: "1%", align: "left", sortable: false, order:"10" },
                    { text: this.$i18n.t("dash.minutesLateDelivery"), value: "minutesLateDelivery", width: "1%", align: "left", sortable: false, order:"11" },
                    { text: this.$i18n.t("dash.extraCMRID"), value: "extraCMRID", width: "1%", align: "left", sortable: false, order:"12" },
                    { text: this.$i18n.t("dash.startLoadingTime"), value: "startLoadingTime", width: "1%", align: "left", sortable: false, order:"13" },
                    { text: this.$i18n.t("dash.stopLoadingTime"), value: "stopLoadingTime", width: "1%", align: "left", sortable: false, order:"14" },
                    { text: this.$i18n.t("dash.startUnloadingTime"), value: "startUnloadingTime", width: "1%", align: "left", sortable: false, order:"15" },
                    { text: this.$i18n.t("dash.stopUnloadingTime"), value: "stopUnloadingTime", width: "1%", align: "left", sortable: false, order:"16" },
                    { text: this.$i18n.t("dash.totalWeight"), value: "totalWeight", width: "1%", align: "left", sortable: false, order:"17" },
                    { text: this.$i18n.t("dash.totalVolume"), value: "totalVolume", width: "1%", align: "left", sortable: false, order:"18" },
                    { text: this.$i18n.t("dash.countGoods"), value: "countGoods", width: "1%", align: "left", sortable: false, order:"19" },
                    { text: this.$i18n.t("dash.countReturnGoods"), value: "countReturnGoods", width: "1%", align: "left", sortable: false, order:"20" },
                    { text: this.$i18n.t("dash.countContainers"), value: "countContainers", width: "1%", align: "left", sortable: false, order:"21" },
                    { text: this.$i18n.t("dash.countAttachments"), value: "countAttachments", width: "1%", align: "center", sortable: false, order: '22' },
                    { text: this.$i18n.t("dash.countScannedGoods"), value: "countScannedGoods", width: "1%", align: "left", sortable: false, order:"23" },
                    { text: this.$i18n.t("dash.countScannedReturnGoods"), value: "countScannedReturnGoods", width: "1%", align: "left", sortable: false, order:"24" },
                    { text: this.$i18n.t("dash.countScannedContainers"), value: "countScannedContainers", width: "1%", align: "left", sortable: false, order:"25" },
                ];
            },
            showHeaders() {
                return this.headers.filter((header) => this.selectedHeaders.find((selectedHeader) => selectedHeader.value === header.value));
            },

            organisationName() {
                return this.allOrganisations.filter((el) => el.id === this.selectedOrganization.id)[0].name;
            },
            filterDateRangeText() {
                return this.filterDates.join(" - ");
            },
        },
        methods: {
            ...mapActions(["GET_DASHBOARD_CMR"]),


            // createChart() {
            //     if(this.statusStatistics.length === 0) return

            //     const series = this.statusStatistics.map(obj => obj.count);
            //     const labels = this.statusStatistics.map(obj => obj.name);

            //     if(this.selectedChartType === 'pie') {

            //         const chartInfo = [
            //             {
            //                 chartInfo: {
            //                     series: series.length > 0 ? series : [1],
            //                     options: {
            //                         chart: {
            //                             type: 'pie',
            //                         },
            //                         title: {
            //                             text: 'CMR statuses',
            //                             align: 'center',
            //                             style: {
            //                                 fontSize: '18px',
            //                                 fontWeight: 'bold',
            //                             },
            //                         },
            //                         labels: labels.length > 0 ? labels : ['No data'],
            //                         fill: {
            //                             opacity: 1,
            //                         },
            //                         legend: {
            //                             position: 'bottom',
            //                         }
            //                     }
            //                 },
            //                 gridInfo: JSON.parse(localStorage.getItem('gridInfo')) || {x: 0, y: 10, w: 12, h: 10, i: 'chart1', static: false}
            //             },
            //         ];

            //         this.chartsData = chartInfo
            //     } else if (this.selectedChartType === 'bar') {
            //         const chartInfo = [
            //             {
            //                 chartInfo: {
            //                     series: [{
            //                         data: series.length > 0 ? series : [1]
            //                     }],
            //                     options: {
            //                         chart: {
            //                             type: 'bar',
            //                         },
            //                         title: {
            //                             text: 'CMR statuses',
            //                             align: 'center',
            //                             style: {
            //                                 fontSize: '18px',
            //                                 fontWeight: 'bold',
            //                             },
            //                         },
            //                         plotOptions: {
            //                             bar: {
            //                                 borderRadius: 4,
            //                                 horizontal: true,
            //                             }
            //                         },
            //                         dataLabels: {
            //                             enabled: false
            //                         },
            //                         xaxis: {
            //                             categories:  labels.length > 0 ? labels : ['No data']
            //                         },
            //                         tooltip: {
            //                             shared: false,
            //                             y: {
            //                                 title: {
            //                                     formatter: function () {
            //                                         return ''
            //                                     }
            //                                 }
            //                             }
            //                         }
            //                     }
            //                 },
            //                 gridInfo: JSON.parse(localStorage.getItem('gridInfo')) || {x: 0, y: 10, w: 12, h: 10, i: 'chart1', static: false}
            //             },
            //         ];

            //         this.chartsData  = chartInfo
            //     } else if (this.selectedChartType === 'column') {
            //         const chartInfo = [
            //             {
            //                 chartInfo: {
            //                     series: [{
            //                         data: series.length > 0 ? series : [1]
            //                     }],
            //                     options: {
            //                         chart: {
            //                             type: 'bar',
            //                         },
            //                         title: {
            //                             text: 'CMR statuses',
            //                             align: 'center',
            //                             style: {
            //                                 fontSize: '18px',
            //                                 fontWeight: 'bold',
            //                             },
            //                         },
            //                         plotOptions: {
            //                             bar: {
            //                                 borderRadius: 4,
            //                                 horizontal: false,
            //                             }
            //                         },
            //                         dataLabels: {
            //                             enabled: false
            //                         },
            //                         xaxis: {
            //                             categories:  labels.length > 0 ? labels : ['No data']
            //                         }
            //                     }
            //                 },
            //                 gridInfo: JSON.parse(localStorage.getItem('gridInfo')) || {x: 0, y: 10, w: 12, h: 10, i: 'chart1', static: false}
            //             },
            //         ];

            //         this.chartsData  = chartInfo
            //     } else if (this.selectedChartType === 'treemap') {
            //         const data = this.statusStatistics.map(obj => ({
            //             x: obj.name,
            //             y: obj.count
            //         }));
            //         const chartInfo = [
            //             {
            //                 chartInfo: {
            //                     series: [
            //                         {
            //                             data: data.length > 0 ? data : [1]
            //                         }
            //                     ],
            //                     options: {
            //                         chart: {
            //                             type: 'treemap',
            //                         },
            //                         title: {
            //                             text: 'CMR statuses',
            //                             align: 'center',
            //                             style: {
            //                                 fontSize: '18px',
            //                                 fontWeight: 'bold',
            //                             },
            //                         },
            //                         legend: {
            //                             show: false
            //                         },
            //                     }
            //                 },
            //                 gridInfo: JSON.parse(localStorage.getItem('gridInfo')) || {x: 0, y: 10, w: 12, h: 10, i: 'chart1', static: false}
            //             },
            //         ];

            //         this.chartsData  = chartInfo
            //     }
            // },



            // createChartSettings(statisticsData) {
            //     if(statisticsData.length === 0) return

            //     const chartSettings = {};
            //     for (let i = 0; i < statisticsData.length; i++) {
            //         const id = `cmrStatus${i + 1}`;
            //         const title = `CMR statuses ${i + 1}`;
            //         const chartTypes = [
            //             { label: 'Pie chart', value: 'pie', selected: true },
            //             { label: 'Bar chart', value: 'bar', selected: false },
            //             { label: 'Column chart', value: 'column', selected: false },
            //             { label: 'Treemap chart', value: 'treemap', selected: false }
            //         ];
            //         chartSettings[id] = { id, chartTypes, addChart: false, title };
            //     }
            //     this.chartSettings = chartSettings;
            // },



            sortingSelectedHeaders(arr){
                this.selectedHeaders = arr.sort((a,b) => a.order - b.order)
            },
            signatureTypeInactive(val, id) {
                const obj = this.filteringCheckboxes.find((el) => el.id === id);
                if (obj.children && !val) {
                    obj.children.forEach((child) => (child[child.id] = val));
                }
            },
            selectedFilteringCheckboxes() {
                const checkboxes = {};
                this.filteringCheckboxes.forEach((item) => {
                    if (item[item.id]) {
                    checkboxes[item.id] = item[item.id];

                    if (item.children) {
                        item.children.forEach((child) => {
                            if (child[child.id]) {
                                if (item.id === 'exceptions') {
                                    if(checkboxes.exceptionList){
                                        checkboxes.exceptionList.push(child.id)
                                    } else{
                                        checkboxes.exceptionList = [child.id]
                                    }
                                } else{
                                    checkboxes[child.id] = child[child.id];
                                }
                            }
                        });
                    }
                    }
                });

                return checkboxes;
            },
            isLoginUserSuperAdmin() {
                return localStorage.getItem("role").toLocaleLowerCase() === "super admin" || localStorage.getItem("role").toLocaleLowerCase() === "super admin plus";
            },
            applyFilter() {
                this.dateTimeBegin = this.filterDates[0]
                this.dateTimeEnd = this.filterDates[1]
                if (this.filterDates.length !== 2) {
                     this.dateTimeBegin = "";
                    this.dateTimeEnd = "";
                }
                this.getDashboardStatistics();
                // this.getStatusStatistics();
                this.GET_DASHBOARD_CMR({
                    ...this.payload,
                    ...this.selectedFilteringCheckboxes(),
                    begin: this.dateTimeBegin,
                    end: this.dateTimeEnd,
                    organizationId: this.subsidiaries ? null : this.selectedOrganization.id,
                    organizationHId: this.subsidiaries ? this.selectedOrganization.organizationsHId : null,
                    onlySender: this.onlySender,
                    onlyCarrier: this.onlyCarrier,
                    onlyReceiver: this.onlyReceiver,
                    carrierCode: this.carrierCode
                });
            },
            getAllOrganizatios() {
                Axios.post( config.apiBaseUrl + "api/organization/dashboard-list", qs.stringify({}),
                    {
                        headers: {
                            Authorization: "Bearer " + localStorage.getItem("accessToken"),
                            "Content-Type": "application/x-www-form-urlencoded",
                        },
                    }
                )
                    .then((response) => {
                        if (response.status === 200) {
                            this.allOrganisations = response.data;
                        }
                    })
                    .catch((error) => {
                    /* eslint-disable */
                        console.log(error);
                    });
            },
            // getStatusStatistics() {
            //         const params = {
            //             ...this.selectedFilteringCheckboxes(),
            //             begin: this.dateTimeBegin,
            //             end: this.dateTimeEnd,
            //             organizationId: this.subsidiaries ? null : this.selectedOrganization.id,
            //             organizationHId: this.subsidiaries ? this.selectedOrganization.organizationsHId : null,
            //             onlySender: this.onlySender,
            //             onlyCarrier: this.onlyCarrier,
            //             onlyReceiver: this.onlyReceiver,
            //             carrierCode: this.carrierCode
            //         }
            //     Axios.get(config.apiBaseUrl + `api/cmr/status-statistics?${qs.stringify(params)}`, {
            //         headers: {
            //             Authorization: "Bearer " + localStorage.getItem("accessToken"),
            //             "Content-Type": "application/x-www-form-urlencoded",
            //         }

            //     })
            //         .then(({ status, data }) => {
            //             if (status === 200) {
            //                 this.statusStatistics = data
            //                 this.createChartSettings([data, data])
            //                 this.createChart()
            //             }
            //         })
            //         .catch((error) => {
            //         /* eslint-disable */
            //             console.log(error);
            //         });

            // },
            getDashboardStatistics() {
                const params = {
                        ...this.selectedFilteringCheckboxes(),
                        begin: this.dateTimeBegin,
                        end: this.dateTimeEnd,
                        organizationId: this.subsidiaries ? null : this.selectedOrganization.id,
                        organizationHId: this.subsidiaries ? this.selectedOrganization.organizationsHId : null,
                        onlySender: this.onlySender,
                        onlyCarrier: this.onlyCarrier,
                        onlyReceiver: this.onlyReceiver,
                        carrierCode: this.carrierCode
                    }
                Axios.get(config.apiBaseUrl + `api/cmr/dashboard-statistics?${qs.stringify(params)}`, {
                    headers: {
                        Authorization: "Bearer " + localStorage.getItem("accessToken"),
                        "Content-Type": "application/x-www-form-urlencoded",
                    }
                })
                    .then(({ status, data }) => {
                        if (status === 200) {
                            this.dashboardStatistics = { ...data };
                            const exceptions = this.filteringCheckboxes.find((item) => item.id === 'exceptions')

                            exceptions.children = this.dashboardStatistics.exceptionType.map(item => {
                                const exist = exceptions.children.find(exception => exception.id === item.name)
                                let checked = false
                                if(exist){
                                    checked = exist[item.name]
                                }
                                return { ...item, id: item.name, [item.name]: checked}
                            })
                        }
                    })
                    .catch((error) => {
                    /* eslint-disable */
                        console.log(error);
                    });
            },
            getDocumentList(id) {
                Axios.get(config.apiBaseUrl + `api/cmr/document-list`, {
                    headers: {
                        Authorization: "Bearer " + localStorage.getItem("accessToken"),
                        "Content-Type": "application/x-www-form-urlencoded",
                    },
                    params: {
                        cmr_id: id
                    },
                })
                    .then(({ status, data }) => {
                        if (status === 200) {
                            this.documentList = data;
                        }
                    })
                    .catch((error) => {
                    /* eslint-disable */
                        console.log(error);
                    });

            },
            downloadDocument(id, type, name) {
                Axios.get(config.apiBaseUrl + `api/cmr/download-document`, {
                    headers: {
                        Authorization: "Bearer " + localStorage.getItem("accessToken"),
                        "Content-Type": "application/x-www-form-urlencoded",
                    },
                    responseType: 'blob',
                    params: {
                        documentId: id,
                        type: type
                    },
                })
                    .then(({ status, data }) => {
                        if (status === 200) {
                            const url = URL.createObjectURL(data)
                            let link = document.createElement("a");
                            link.download = name;
                            link.href = url;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            link = null
                        }
                    })
                    .catch((error) => {
                    /* eslint-disable */
                        console.log(error);
                    });

            },
            downloadCSV() {
                let payload = { ...this.payload }
                payload.Limit = 999999999
                Axios.post(config.apiBaseUrl + `api/cmr/dashboard-csv`, qs.stringify({
                    ...payload,
                    ...this.selectedFilteringCheckboxes(),
                    begin: this.dateTimeBegin,
                    end: this.dateTimeEnd,
                    organizationId: this.subsidiaries ? null : this.selectedOrganization.id,
                    organizationHId: this.subsidiaries ? this.selectedOrganization.organizationsHId : null,
                    onlySender: this.onlySender,
                    onlyCarrier: this.onlyCarrier,
                    onlyReceiver: this.onlyReceiver,
                    carrierCode: this.carrierCode
                }),{
                    headers: {
                        Authorization: "Bearer " + localStorage.getItem("accessToken"),
                        "Content-Type": "application/x-www-form-urlencoded",
                    },
                    responseType: 'blob',
                })
                    .then(({ status, data }) => {
                        if (status === 200) {
                            const url = URL.createObjectURL(data)
                            let link = document.createElement("a");
                            link.download = 'dashboard-data.csv';
                            link.href = url;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            link = null
                        }
                    })
                    .catch((error) => {
                    /* eslint-disable */
                        console.log(error);
                    });

            },
            setCurrentMonth() {
                const date = new Date();
                const y = date.getFullYear(),
                    m =
                    date.getMonth() < 9
                        ? "0" + (date.getMonth() + 1)
                        : date.getMonth() + 1,
                    d = date.getDate() < 10 ? "0" + date.getDate() : date.getDate();
                this.dateTimeBegin = `${y}-${m}-01`;
                this.dateTimeEnd = `${y}-${m}-${d}`;
                this.filterDates = [this.dateTimeBegin, this.dateTimeEnd];
            },
            removeFilterDate() {
                this.dateTimeBegin = "";
                this.dateTimeEnd = "";
                this.filterDates = [];
                this.getDashboardStatistics();
                // this.getStatusStatistics();
                this.GET_DASHBOARD_CMR({
                    ...this.payload,
                    ...this.selectedFilteringCheckboxes(),
                    begin: this.dateTimeBegin,
                    end: this.dateTimeEnd,
                    organizationId: this.subsidiaries ? null : this.selectedOrganization.id,
                    organizationHId: this.subsidiaries ? this.selectedOrganization.organizationsHId : null,
                    onlySender: this.onlySender,
                    onlyCarrier: this.onlyCarrier,
                    onlyReceiver: this.onlyReceiver,
                    carrierCode: this.carrierCode
                });
            },
            removeFilterOrganisation() {
                this.selectedOrganization = {};
                this.getDashboardStatistics();
                // this.getStatusStatistics();
                this.GET_DASHBOARD_CMR({
                    ...this.payload,
                    ...this.selectedFilteringCheckboxes(),
                    begin: this.dateTimeBegin,
                    end: this.dateTimeEnd,
                    organizationId: this.subsidiaries ? null : this.selectedOrganization.id,
                    organizationHId: this.subsidiaries ? this.selectedOrganization.organizationsHId : null,
                    onlySender: this.onlySender,
                    onlyCarrier: this.onlyCarrier,
                    onlyReceiver: this.onlyReceiver,
                    carrierCode: this.carrierCode
                });
            },
            removeFilterAll() {
                this.dateTimeBegin = "";
                this.dateTimeEnd = "";
                this.filterDates = [];
                this.selectedOrganization = {};
                this.carrierCode = "";
                this.getDashboardStatistics();
                // this.getStatusStatistics();
                this.GET_DASHBOARD_CMR({
                    ...this.payload,
                    ...this.selectedFilteringCheckboxes(),
                    begin: this.dateTimeBegin,
                    end: this.dateTimeEnd,
                    organizationId: this.subsidiaries ? null : this.selectedOrganization.id,
                    organizationHId: this.subsidiaries ? this.selectedOrganization.organizationsHId : null,
                    onlySender: this.onlySender,
                    onlyCarrier: this.onlyCarrier,
                    onlyReceiver: this.onlyReceiver,
                    carrierCode: this.carrierCode
                });
            },
            openAttachmentsDialog(id) {
                this.getDocumentList(id)
                this.attchmentsDialog = true
            },
            closeAttachmentsDialog() {
                this.attchmentsDialog = false
                this.documentList = []
            },
            statusNameColor(item) {
                let color
                switch (item.status) {
                    case 1:
                        color = '#230bf0'
                        break
                    case 2:
                        color = '71a951'
                        break
                    case 3:
                        color = '#f8d619'
                        break
                    case 4:
                        color = '#ee19f8'
                        break
                    case 5:
                        color = '#8b0bf0'
                        break
                    case 6:
                        color = '#0bedf0'
                        break
                    case 7:
                        color = '#078b8d'
                        break
                    case 8:
                        color = '#078b8d'
                        break
                    case 9:
                        color = '#5b8ff5'
                        break
                    default:
                        color = '#edf00b'
                        break
                }
                return {color: color}
            },
            openCompare() {
                this.$router.push({name: 'comparePro'})
            }

            //------------ end methods
        },
        watch: {
            // chartsData: {
            //     handler(value) {
            //         localStorage.setItem('gridInfo', JSON.stringify(value[0].gridInfo))
            //     },
            //     deep: true
            // },
            // chartSettings: {
            //     handler(value) {
            //         console.log('chartSettings >>>', value);
            //     },
            //     deep: true

            // },
            // selectedChartType(chartType) {
            //     localStorage.setItem('selectedChartType', chartType)
            //     this.createChart()
            // },
            payload(v) {
                this.GET_DASHBOARD_CMR({
                    ...v,
                    ...this.selectedFilteringCheckboxes(),
                    begin: this.dateTimeBegin,
                    end: this.dateTimeEnd,
                    organizationId: this.subsidiaries ? null : this.selectedOrganization.id,
                    organizationHId: this.subsidiaries ? this.selectedOrganization.organizationsHId : null,
                    onlySender: this.onlySender,
                    onlyCarrier: this.onlyCarrier,
                    onlyReceiver: this.onlyReceiver,
                    carrierCode: this.carrierCode
                });
            },
            selectedHeaders(){
                window.localStorage.selectedHeaders = JSON.stringify(this.selectedHeaders)
            },
            selectedOrganization(organization) {
                if(!organization.id) {
                    this.subsidiaries = false
                    this.onlySender = false
                    this.onlyCarrier = false
                    this.onlyReceiver = false
                }
            }
        },
        created() {
            if (!window.localStorage.selectedHeaders) {
                window.localStorage.selectedHeaders = JSON.stringify(this.headers)
            }
            this.selectedHeaders = JSON.parse(window.localStorage.selectedHeaders).sort((a,b) => a.order - b.order);
            this.setCurrentMonth();
            this.getAllOrganizatios();
            this.getDashboardStatistics();
            // this.getStatusStatistics();
            // console.log('>>>>>>>>>', JSON.parse(localStorage.getItem('gridInfo')));
        },
    };
</script>
